import { getCarrierDisplayName } from "@redotech/redo-model/order-tracking/carriers/carrier-resolver";
import { OrderTrackingShipmentStatus } from "@redotech/redo-model/order-tracking/order-tracking-types";
import type { TrackingPageShipment } from "@redotech/redo-model/order-tracking/tracking-page-order-definition";
import { createElement, memo } from "react";
import type { PickupConfig } from "../../../settings-elements/order/pick-up-config-util";
import { getCarrierIcon } from "./get-carrier-icon";

const STATUS_STRINGS: Record<OrderTrackingShipmentStatus, string> = {
  [OrderTrackingShipmentStatus.PRE_TRANSIT]: "Pre-transit",
  [OrderTrackingShipmentStatus.PENDING]: "Pending",
  [OrderTrackingShipmentStatus.IN_TRANSIT]: "In transit",
  [OrderTrackingShipmentStatus.OUT_FOR_DELIVERY]: "Out for delivery",
  [OrderTrackingShipmentStatus.DELIVERED]: "Delivered",
  [OrderTrackingShipmentStatus.AVAILABLE_FOR_PICKUP]: "Available for pickup",
  [OrderTrackingShipmentStatus.AVAILABLE_FOR_PICKUP_CARRIER]:
    "Available for pickup",
  [OrderTrackingShipmentStatus.STALLED_IN_TRANSIT]: "Stalled in transit",
  [OrderTrackingShipmentStatus.RETURN_TO_SENDER]: "Return to sender",
  [OrderTrackingShipmentStatus.DELIVERY_ATTEMPTED]: "Delivery attempted",
  [OrderTrackingShipmentStatus.DELIVERY_FAILURE]: "Delivery failure",
  [OrderTrackingShipmentStatus.SHIPMENT_CANCELLED]: "Cancelled",
  [OrderTrackingShipmentStatus.SHIPMENT_ERROR]: "Error",
  [OrderTrackingShipmentStatus.UNKNOWN]: "Pre-transit",
};

function isStalledInTransit(
  shipment: TrackingPageShipment,
  stalledDays: number | null,
): boolean {
  if (stalledDays === null || stalledDays <= 0) return false;
  const updatedAt = shipment.updatedAt;
  if (!updatedAt) return false;
  const daysSinceUpdate =
    (Date.now() - new Date(updatedAt).getTime()) / (1000 * 60 * 60 * 24);
  return daysSinceUpdate >= stalledDays;
}

// Derive the status copy locally so the live block doesn't depend on
// i18n being initialized (e.g. inside Storybook). The cancel-reason and
// merchant-specific override branches still flow through ShipmentStatusDisplay
// in the customer portal — those are handled at the call site.
export function getShipmentStatusText({
  shipment,
  cancelReason,
  source,
  pickupConfig,
  stalledDays,
}: {
  shipment: TrackingPageShipment | undefined;
  cancelReason?: string | null;
  source?: string | null;
  pickupConfig: PickupConfig;
  stalledDays: number | null;
}): string {
  if (cancelReason) {
    return "Your order has been cancelled, please contact customer support for inquiries";
  }

  const { isPickUpOrder, readyForPickUp, hasBeenPickedUp } = pickupConfig;
  if (isPickUpOrder && readyForPickUp) return "Ready for pickup";
  if (isPickUpOrder && hasBeenPickedUp) return "Picked up";

  if (!shipment) {
    if (source === "pos") return "Purchased in store";
    return "Your order is being prepared";
  }

  if (
    shipment.status === OrderTrackingShipmentStatus.STALLED_IN_TRANSIT ||
    (shipment.status === OrderTrackingShipmentStatus.IN_TRANSIT &&
      isStalledInTransit(shipment, stalledDays))
  ) {
    return "Stalled in transit";
  }

  return STATUS_STRINGS[shipment.status] ?? "Pre-transit";
}

export const CarrierBrandIcon = memo(function CarrierBrandIcon({
  carrier,
}: {
  carrier: string | null;
}) {
  const Icon = getCarrierIcon(carrier);
  const label = carrier ? getCarrierDisplayName(carrier) : "Carrier";
  return createElement(Icon, {
    "aria-label": `Carrier: ${label}`,
    height: 24,
    style: { display: "block", flexShrink: 0 },
  });
});

export function formatEventTimestamp(timestamp: Date | undefined) {
  if (!timestamp) return "";
  try {
    const instant = timestamp.toTemporalInstant();
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return instant.toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
      timeZone: tz,
    });
  } catch {
    return "";
  }
}

export function formatEventMessage(e: {
  message: string;
  location?: unknown;
}): string {
  const loc = e.location;
  const parts: string[] = [];
  if (loc && typeof loc !== "string") {
    const l = loc as { city?: string; state?: string; country?: string };
    if (l.city) parts.push(l.city);
    if (l.state) parts.push(l.state);
    if (l.country) parts.push(l.country);
  } else if (typeof loc === "string" && loc) {
    parts.push(loc);
  }
  return parts.length > 0 ? `${e.message}, ${parts.join(", ")}` : e.message;
}
